import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    BehaviorSubject,
    map,
    mergeMap,
    Observable,
    of,
    take,
    tap,
} from 'rxjs';
import {
    ApplicationCreationResponse,
    ApplicationCreationStatus,
} from '../../models/responses/application-creation-response';
import { ApplicationService } from '../application/application.service';
import { ApplicationCreationRequest } from '../../models/requests/application-creation-request';
import { FormGroupType } from '../../../../../core/models/form-group-type';
import { DirectEntryStepService } from '../direct-entry-step/direct-entry-step.service';
import { Router } from '@angular/router';
import { StepLink } from '../../models/step-link';
import {
    ApplicationPurposeCodeEnum,
    isUnsupportedPurpose,
} from '../../constants/application-purpose-codes';
import { AlertService } from '../../../../../core/services/alert/alert.service';
import { LargeApplicationNotificationMessages } from '../../../purpose-of-filing/constants/purpose-of-filling-messages';
import { RouteConstants } from '../../../../../core/constants/routes';
import { ApplicationAdvisorViewNavigationState } from '../../models/application-advisor-view-navigation-state';

@Injectable({
    providedIn: 'root',
})
export class ApplicationPreparationQuestionnaireService {
    private readonly applicationService = inject(ApplicationService);
    private readonly directEntryStepService = inject(DirectEntryStepService);
    private readonly router = inject(Router);
    private readonly alertService = inject(AlertService);

    private applicationPreparationForm: FormGroup<
        FormGroupType<ApplicationCreationRequest>
    > = new FormGroup<FormGroupType<ApplicationCreationRequest>>({
        applicationPurpose: new FormControl<ApplicationPurposeCodeEnum | null>(
            null,
            Validators.required,
        ),
        fccRegistrationNumber: new FormControl<string | null>(null),
        radioServiceCode: new FormControl<string | null>(null),
        applicantTypeCode: new FormControl<string | null>(null),
        civiId: new FormControl<number | null>(null),
        callSign: new FormControl<string | null>(null),
        templateApplicationMasterId: new FormControl<number | null>(null),
    });

    /**
     * Returns the preparation form for creating an application.
     * @returns The application preparation form.
     */
    getApplicationPreparationForm(): FormGroup<
        FormGroupType<ApplicationCreationRequest>
    > {
        return this.applicationPreparationForm;
    }

    /**
     * Updates the application preparation form with the filled out information from the individual step forms.
     * @param request
     */
    patchApplicationPreparationForm(
        stepFormValues: Partial<ApplicationCreationRequest>,
    ): void {
        this.applicationPreparationForm.patchValue(stepFormValues);
    }

    /**
     * Clears the civiId while preserving other in-progress form values.
     */
    clearCiviId(): void {
        this.applicationPreparationForm.controls.civiId.setValue(null);
    }

    /**
     * Submits the application preparation form to create a new application with the provided information.
     * @param hasTimerElapsed$
     */
    submitApplicationPreparationForm(
        hasTimerElapsed$: BehaviorSubject<boolean>,
    ): Observable<ApplicationCreationStatus> {
        const request: ApplicationCreationRequest =
            this.generateRequestObject();

        const purpose = request.applicationPurpose;
        const isUnsupportedApplication = isUnsupportedPurpose(purpose);

        return this.applicationService.createApplication(request).pipe(
            mergeMap((applicationResponse: ApplicationCreationResponse) => {
                switch (applicationResponse.status) {
                    case ApplicationCreationStatus.CallSignNotFound:
                    // falls through
                    case ApplicationCreationStatus.Timeout:
                        return of(applicationResponse.status);
                    case ApplicationCreationStatus.Success:
                        if (isUnsupportedApplication) {
                            const id = String(
                                applicationResponse.applicationId,
                            );
                            this.router.navigate(
                                [
                                    RouteConstants.Application.getFullRouteWithApplicationId(
                                        'unsupported',
                                        id,
                                    ),
                                ],
                                {
                                    state: {
                                        justCreated: true,
                                    } as ApplicationAdvisorViewNavigationState,
                                },
                            );
                            return of(applicationResponse.status);
                        }

                        if (hasTimerElapsed$.getValue()) {
                            this.displayApplicationAvailableAlert();
                            return of(applicationResponse.status);
                        } else {
                            return this.directEntryStepService
                                .getDirectEntryStepLinksForApplication(
                                    applicationResponse.applicationId!.toString(),
                                )
                                .pipe(
                                    tap((steps: StepLink[]) => {
                                        this.resetApplicationPreparationForm();
                                        this.router.navigate([steps[0].url], {
                                            state: {
                                                justCreated: true,
                                            } as ApplicationAdvisorViewNavigationState,
                                        });
                                    }),
                                    map(() => applicationResponse.status),
                                );
                        }
                    default:
                        throw new Error(
                            `Unsupported application status: ${applicationResponse.status}`,
                        );
                }
            }),
            take(1),
        );
    }

    private displayApplicationAvailableAlert(): void {
        this.alertService.displaySuccessAlert(
            LargeApplicationNotificationMessages.ApplicationAvailableMessage,
            true,
        );
    }

    private resetApplicationPreparationForm(): void {
        this.applicationPreparationForm.reset();
    }

    private generateRequestObject(): ApplicationCreationRequest {
        return this.applicationPreparationForm
            .value as ApplicationCreationRequest;
    }
}
